import { useState } from 'react';
import { Ruler, Scale, Dumbbell, Activity, ChevronRight } from 'lucide-react';

import { api } from '../lib/api.js';
import { Link } from 'react-router-dom';
import { useApiData } from '../lib/hooks.js';
import { useToast } from '../lib/store.js';
import { isoToday, fmtDate, kg, cm, toStone, num, signed } from '../lib/format.js';
import { Card, Button, Sheet, Field, NumberInput, TextInput, TextArea, Spinner, ErrorNote, Sparkline, SectionTitle, PageHeader, Pill } from '../components/ui.jsx';

// Measurements and clothing fit lead. Weight is one card among several, and
// everything it says is said about the smoothed line rather than a reading.

const MEASURES = [
  ['waist_cm', 'Waist'],
  ['hips_cm', 'Hips'],
  ['bust_cm', 'Bust'],
  ['thigh_cm', 'Thigh'],
  ['arm_cm', 'Upper arm'],
];

export default function Progress() {
  const { data, loading, error, reload } = useApiData('/progress');
  const [sheet, setSheet] = useState(null);

  if (loading && !data) return <Spinner label="Loading progress" />;
  if (error) return <div className="p-gutter"><ErrorNote error={error} onRetry={reload} /></div>;
  if (!data) return null;

  const { weight, measurements, training, profile } = data;

  return (
    <div className="max-w-lg mx-auto">
      <PageHeader
        title="Progress"
        subtitle={`Week ${data.week} of ${data.totalWeeks} · ${data.daysToTarget > 0 ? `${num(data.daysToTarget)} days to go` : 'target date reached'}`}
      />

      <div className="px-gutter">
        {/* Shape first — this is the thing the programme is actually for. */}
        <SectionTitle action={<Button size="sm" variant="ghost" onClick={() => setSheet('measure')}>Add</Button>}>
          Shape and fit
        </SectionTitle>
        <Card className="mb-5">
          {measurements.latest ? (
            <>
              <div className="grid grid-cols-2 gap-4 mb-4">
                {MEASURES.filter(([k]) => measurements.latest[k] != null).map(([k, label]) => (
                  <div key={k}>
                    <p className="text-label uppercase text-textMute mb-1">{label}</p>
                    <p className="text-lg font-semibold">{cm(measurements.latest[k])}</p>
                    {measurements.change[k] != null && (
                      <p className={`text-meta ${measurements.change[k] < 0 ? 'text-good' : 'text-textMute'}`}>
                        {signed(measurements.change[k], 'cm')} since the start
                      </p>
                    )}
                  </div>
                ))}
              </div>
              {measurements.latest.clothing_size && (
                <div className="pt-3 border-t border-edge">
                  <p className="text-body-sm">
                    Clothes: <strong>{measurements.latest.clothing_size}</strong>
                    {profile.clothing_goal && <span className="text-textMute"> · heading for {profile.clothing_goal}</span>}
                  </p>
                  {measurements.latest.fit_note && <p className="text-meta text-textMute mt-1">{measurements.latest.fit_note}</p>}
                </div>
              )}
              <p className="text-meta text-textMute mt-3">Last taken {fmtDate(measurements.latest.date, 'long')}. Once a month is plenty.</p>
            </>
          ) : (
            <div className="text-center py-4">
              <Ruler size={24} className="mx-auto mb-2 text-textMute" strokeWidth={1.5} />
              <p className="text-body-sm text-textSoft mb-3">No measurements yet. Waist and hips tell you more about this programme than the scales do.</p>
              <Button size="sm" onClick={() => setSheet('measure')}>Take the first ones</Button>
            </div>
          )}
        </Card>

        <SectionTitle action={<Button size="sm" variant="ghost" onClick={() => setSheet('weight')}>Add</Button>}>Weight</SectionTitle>
        <Card className="mb-5">
          {weight.latest ? (
            <>
              <div className="flex items-baseline gap-3 mb-1">
                <span className="text-hero-num">{weight.latest.kg.toFixed(1)}</span>
                <span className="text-body text-textMute">kg</span>
              </div>
              <p className="text-body-sm text-textSoft mb-4">
                {toStone(weight.latest.kg)}
                {weight.totalChange != null && (
                  <span className={weight.totalChange < 0 ? 'text-good' : 'text-textMute'}> · {signed(weight.totalChange, 'kg')} since the start</span>
                )}
              </p>
              <Sparkline points={weight.smoothed.map((p) => ({ date: p.date, value: p.avg }))} label="Weight, seven-day average" />
              <p className="text-meta text-textMute mt-2">
                Seven-day average, not daily readings — day to day it swings around by a kilo for reasons that have nothing to do with fat.
              </p>
              {weight.trend28.direction !== 'unknown' && (
                <p className="text-body-sm text-textSoft mt-3 pt-3 border-t border-edge">
                  {weight.trend28.direction === 'down'
                    ? `Down about ${Math.abs(weight.trend28.changePerWeek).toFixed(2)}kg a week over the last month. That is the pace this programme is built for.`
                    : weight.trend28.direction === 'up'
                      ? 'Up slightly over the last month. Worth reading alongside your measurements before changing anything.'
                      : 'Flat over the last month. If the tape measure is moving, the scales are the less useful number.'}
                </p>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <Scale size={24} className="mx-auto mb-2 text-textMute" strokeWidth={1.5} />
              <p className="text-body-sm text-textSoft mb-3">No weight logged yet.</p>
              <Button size="sm" onClick={() => setSheet('weight')}>Log it</Button>
            </div>
          )}
        </Card>

        <SectionTitle>Migraines</SectionTitle>
        <Link to="/migraines" className="card flex items-center gap-3 mb-5 hover:border-edgeHi transition-colors">
          <Activity size={20} className="text-accent shrink-0" />
          <div className="flex-1">
            <p className="text-body-sm font-semibold">Log one, and see what they line up with</p>
            <p className="text-meta text-textMute">Food, training, sleep, hydration and alcohol, compared against your own logs.</p>
          </div>
          <ChevronRight size={18} className="text-textMute shrink-0" />
        </Link>

        <SectionTitle>Training</SectionTitle>
        <Card className="mb-5">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-label uppercase text-textMute mb-1">Sessions</p>
              <p className="text-lg font-semibold">{training.sessionsDone}</p>
            </div>
            <div>
              <p className="text-label uppercase text-textMute mb-1">Weeks trained</p>
              <p className="text-lg font-semibold">{training.weeksTrained}</p>
            </div>
            <div>
              <p className="text-label uppercase text-textMute mb-1">Run of weeks</p>
              <p className="text-lg font-semibold">{training.currentStreak}</p>
            </div>
          </div>
          <p className="text-meta text-textMute">
            A week counts once you have done a single session in it. Two out of three is a good week; one is still a week.
          </p>

          {training.lifts.length > 0 && (
            <div className="mt-4 pt-4 border-t border-edge">
              <p className="text-label uppercase text-textMute mb-3">Getting stronger</p>
              <ul className="space-y-2.5">
                {training.lifts
                  .filter((l) => l.latest?.weight != null)
                  .map((l) => (
                    <li key={l.exerciseId} className="flex items-center gap-3">
                      <Dumbbell size={15} className="text-textMute shrink-0" />
                      <span className="text-body-sm flex-1 capitalize">{l.exerciseId.replace(/-/g, ' ')}</span>
                      <span className="text-body-sm font-semibold">{l.latest.weight}kg</span>
                      {l.change != null && l.change !== 0 && <Pill tone={l.change > 0 ? 'good' : 'mute'}>{signed(l.change, 'kg', 1)}</Pill>}
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </Card>
      </div>

      <MeasureSheet open={sheet === 'measure'} onClose={() => setSheet(null)} onSaved={() => { setSheet(null); reload({ quiet: true }); }} latest={measurements.latest} />
      <WeightSheet open={sheet === 'weight'} onClose={() => setSheet(null)} onSaved={() => { setSheet(null); reload({ quiet: true }); }} />
    </div>
  );
}

function MeasureSheet({ open, onClose, onSaved, latest }) {
  const { show } = useToast();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  return (
    <Sheet open onClose={onClose} title="Measurements">
      <p className="text-meta text-textMute mb-4">
        Same place, same time of day, tape snug but not pulled tight. Once a month is enough — these move slowly and that is fine.
      </p>
      <div className="grid grid-cols-2 gap-3">
        {MEASURES.map(([k, label]) => (
          <Field key={k} label={label} id={k}>
            <NumberInput
              id={k}
              suffix="cm"
              step="0.5"
              placeholder={latest?.[k] != null ? String(latest[k]) : ''}
              value={form[k] ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
            />
          </Field>
        ))}
      </div>
      <Field label="Clothes size" id="cl">
        <TextInput id="cl" placeholder={latest?.clothing_size || 'UK 12'} value={form.clothing_size ?? ''} onChange={(e) => setForm((f) => ({ ...f, clothing_size: e.target.value }))} />
      </Field>
      <Field label="How are things fitting?" id="fit" hint="The most useful field on this page. Worth a sentence.">
        <TextArea id="fit" rows={2} value={form.fit_note ?? ''} onChange={(e) => setForm((f) => ({ ...f, fit_note: e.target.value }))} placeholder="e.g. black jeans do up without a fight now." />
      </Field>
      <Button
        className="w-full"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await api.post('/logs/measurements', { date: isoToday(), ...form });
            show('Saved.');
            onSaved();
          } catch (err) {
            show(err.message, 'crit');
          } finally {
            setBusy(false);
          }
        }}
      >
        Save
      </Button>
    </Sheet>
  );
}

function WeightSheet({ open, onClose, onSaved }) {
  const { show } = useToast();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  return (
    <Sheet open onClose={onClose} title="Weight">
      <Field label="Weight" id="wq" hint="First thing in the morning gives the most comparable number.">
        <NumberInput id="wq" suffix="kg" step="0.1" value={value} onChange={(e) => setValue(e.target.value)} autoFocus />
      </Field>
      {value && <p className="text-body-sm text-textSoft mb-4">{toStone(Number(value))}</p>}
      <Button
        className="w-full"
        disabled={busy || !value}
        onClick={async () => {
          setBusy(true);
          try {
            await api.post('/logs/weight', { date: isoToday(), kg: Number(value) });
            show('Saved.');
            onSaved();
          } catch (err) {
            show(err.message, 'crit');
          } finally {
            setBusy(false);
          }
        }}
      >
        Save
      </Button>
    </Sheet>
  );
}
